/**
 * AssignedDevicesSummary.js
 *
 * Phase 4: Assigned Devices Summary for Technician Dashboard
 * Displays quick overview of devices assigned to the engineer without navigating away
 *
 * Features:
 * - Total devices count (Primary vs Secondary breakdown)
 * - Devices with active issues count
 * - Devices by department distribution
 * - Quick action to navigate to full My Devices page
 *
 * Props:
 * - devices: Array of device objects assigned to the engineer
 * - token: JWT authentication token
 * - onViewMyDevices: Callback when "View All Devices" button is clicked
 */

import { useMemo } from 'react';

const deviceHasIssues = (device) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  if (device.ppm_next_due_date) {
    const ppmDate = new Date(device.ppm_next_due_date);
    if (ppmDate < today) return true;
  }

  if (device.calibration_next_due_date) {
    const calibDate = new Date(device.calibration_next_due_date);
    if (calibDate < today) return true;
  }

  if (device.active_complaint_count && device.active_complaint_count > 0) {
    return true;
  }

  return false;
};

const AssignedDevicesSummary = ({ devices = [], onViewMyDevices }) => {
  // Calculate statistics
  const stats = useMemo(() => {
    if (devices.length === 0) {
      return {
        totalDevices: 0,
        primaryDevices: 0,
        secondaryDevices: 0,
        devicesWithIssues: 0,
        departmentDistribution: [],
        departmentCount: 0
      };
    }

    // Count primary and secondary assignments
    const primaryDevices = devices.filter(d => d.is_primary_technician).length;
    const secondaryDevices = devices.filter(d => !d.is_primary_technician).length;

    const devicesWithIssues = devices.filter(deviceHasIssues).length;

    // Per-department breakdown with assignment and issue stats
    const departmentMap = {};
    devices.forEach((device) => {
      const deptName = device.department || 'Unassigned';
      if (!departmentMap[deptName]) {
        departmentMap[deptName] = {
          name: deptName,
          icon: device.department_icon || '🏢',
          count: 0,
          primary: 0,
          secondary: 0,
          issues: 0,
        };
      }
      const entry = departmentMap[deptName];
      entry.count += 1;
      if (device.is_primary_technician) {
        entry.primary += 1;
      } else {
        entry.secondary += 1;
      }
      if (deviceHasIssues(device)) {
        entry.issues += 1;
      }
    });

    const departmentDistribution = Object.values(departmentMap)
      .map((dept) => ({
        ...dept,
        percentage: Math.round((dept.count / devices.length) * 100),
      }))
      .sort((a, b) => b.count - a.count);

    return {
      totalDevices: devices.length,
      primaryDevices,
      secondaryDevices,
      devicesWithIssues,
      departmentDistribution,
      departmentCount: departmentDistribution.length
    };
  }, [devices]);

  // Handle empty state
  if (stats.totalDevices === 0) {
    return (
      <div className="assigned-devices-summary">
        <h3 className="dashboard-section-title">📱 Assigned Devices</h3>
        <div className="summary-empty-state">
          <p className="empty-icon">📭</p>
          <p className="empty-title">No devices assigned</p>
          <p className="empty-subtitle">You currently have no assigned devices</p>
        </div>
      </div>
    );
  }

  return (
    <div className="assigned-devices-summary">
      <h3 className="dashboard-section-title">📱 Assigned Devices</h3>

      {/* Quick Stats Grid */}
      <div className="assigned-devices-stats-grid">
        {/* Department Card - Show department name if single department, OR department count if multiple */}
        {stats.departmentCount === 1 ? (
          <div className="assigned-stat-card department-primary">
            <div className="stat-icon">{stats.departmentDistribution[0].icon}</div>
            <div className="stat-content">
              <div className="stat-value-text">{stats.departmentDistribution[0].name}</div>
              <div className="stat-label">Department</div>
            </div>
          </div>
        ) : (
          <div className="assigned-stat-card departments">
            <div className="stat-icon">🏢</div>
            <div className="stat-content">
              <div className="stat-value">{stats.departmentCount}</div>
              <div className="stat-label">Departments with Devices</div>
            </div>
          </div>
        )}

        {/* Total Devices Card */}
        <div className="assigned-stat-card total">
          <div className="stat-icon">🖥️</div>
          <div className="stat-content">
            <div className="stat-value">{stats.totalDevices}</div>
            <div className="stat-label">Total Devices</div>
          </div>
        </div>

        {/* Assignment Breakdown Card */}
        <div className="assigned-stat-card assignment">
          <div className="stat-icon">👨‍🔧</div>
          <div className="stat-content">
            <div className="stat-breakdown">
              <span className="primary-count">
                <span className="badge-icon primary">🔵</span>
                {stats.primaryDevices}
              </span>
              <span className="secondary-count">
                <span className="badge-icon secondary">⚪</span>
                {stats.secondaryDevices}
              </span>
            </div>
            <div className="stat-label">Primary / Secondary</div>
          </div>
        </div>

        {/* Active Issues Card */}
        <div className={`assigned-stat-card issues ${stats.devicesWithIssues > 0 ? 'has-issues' : ''}`}>
          <div className="stat-icon">{stats.devicesWithIssues > 0 ? '⚠️' : '✅'}</div>
          <div className="stat-content">
            <div className="stat-value">{stats.devicesWithIssues}</div>
            <div className="stat-label">Devices with Issues</div>
          </div>
        </div>
      </div>

      {/* Department Distribution - compact per-department cards */}
      {stats.departmentCount > 1 && (
        <div className="department-distribution">
          <div className="distribution-header">
            <h4 className="distribution-title">Devices by Department</h4>
            <p className="distribution-subtitle">
              {stats.totalDevices} device{stats.totalDevices !== 1 ? 's' : ''} across{' '}
              {stats.departmentCount} department{stats.departmentCount !== 1 ? 's' : ''} where you have assignments
            </p>
          </div>
          <div className="dept-distribution-grid">
            {stats.departmentDistribution.map((dept) => (
              <div
                key={dept.name}
                className={`dept-distribution-card${dept.issues > 0 ? ' has-issues' : ''}`}
                title={`${dept.name}: ${dept.count} device${dept.count !== 1 ? 's' : ''} (${dept.percentage}%)`}
              >
                <div className="dept-distribution-card__icon">{dept.icon}</div>
                <div className="dept-distribution-card__name">{dept.name}</div>
                <div className="dept-distribution-card__total">
                  <span className="dept-distribution-card__count">{dept.count}</span>
                  <span className="dept-distribution-card__pct">{dept.percentage}%</span>
                </div>
                <div className="dept-distribution-card__breakdown">
                  <span title="Primary assignments">
                    <span className="badge-icon primary">🔵</span>
                    {dept.primary}
                  </span>
                  <span title="Secondary assignments">
                    <span className="badge-icon secondary">⚪</span>
                    {dept.secondary}
                  </span>
                </div>
                <div className={`dept-distribution-card__issues${dept.issues > 0 ? ' active' : ''}`}>
                  {dept.issues > 0 ? `⚠️ ${dept.issues} issue${dept.issues !== 1 ? 's' : ''}` : '✅ No issues'}
                </div>
                <div className="dept-distribution-card__share-bar" aria-hidden="true">
                  <div
                    className="dept-distribution-card__share-fill"
                    style={{ width: `${Math.max(dept.percentage, 4)}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Action Button */}
      <div className="summary-action">
        <button
          className="btn-view-all-devices"
          onClick={onViewMyDevices}
          title="View full list of assigned devices"
        >
          View all devices ›
        </button>
      </div>
    </div>
  );
};

export default AssignedDevicesSummary;
